import React from 'react';
import { FormInstance, Select, TimePicker } from 'antd';
import {
  datetime,
  dayjsToMoment,
  isDatetime,
  momentToDayjs,
  type DatetimeType,
  type Moment,
} from '@common/services/datetime';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { OrganizationUnitShiftConfigFlatType } from '@scheduling/graphql';

export const TimeInput = ({
  disabled = false,
  shiftConfigs = [],
  form,
  onTimeSelect,
  startTime,
}: {
  disabled: boolean;
  shiftConfigs: OrganizationUnitShiftConfigFlatType[];
  form: FormInstance;
  onTimeSelect: (value: string) => void;
  startTime?: string;
}) => {
  const shiftStartDisabledTime = (currentDate: Moment) => {
    // currentDate is antd's raw Moment (disabledDate); selectedDate is the
    // form's bridged Moment (see start-datetime-select.tsx). datetime()
    // handles a Moment object at runtime (createDatetime's moment-object
    // branch) and, unlike momentToDayjs, is never typed nullable.
    const selectedDate = form.getFieldValue('startDate');

    if (datetime(selectedDate).startOf('day') > datetime().startOf('day')) {
      return false;
    }

    return momentToDayjs(currentDate) <= datetime();
  };

  const options = shiftConfigs?.map((shiftConfig) => {
    const formatted = datetime(shiftConfig.startTime).format('HH:mm');
    return { label: formatted, value: formatted };
  });

  const onSelect = (value: Moment | DatetimeType | null) => {
    // value is a raw antd Moment from the TimePicker, or already a
    // DatetimeType from the Select branch below — bridge only the Moment case.
    const asDayjs =
      value === null || isDatetime(value) ? value : momentToDayjs(value);
    const formattedValue = datetime(asDayjs, 'HH:mm').format('HH:mm');

    onTimeSelect(formattedValue);
    form.setFieldsValue({
      startTime: formattedValue,
    });
  };

  if (shiftConfigs.length > 0) {
    return (
      <Select
        disabled={disabled}
        showSearch
        size="large"
        placeholder="Select time"
        getPopupContainer={(trigger) => trigger.parentNode}
        options={uniqBy(sortBy(options, 'value'), 'value')}
        onSelect={(value) => onSelect(datetime(value, 'HH:mm'))}
        value={startTime}
      />
    );
  }

  return (
    <>
      <TimePicker
        name="startTime"
        format="HH:mm"
        size="large"
        showNow={false}
        disabled={disabled}
        disabledDate={shiftStartDisabledTime}
        minuteStep={15}
        hideDisabledOptions={true}
        // Raw antd v4 TimePicker value prop, moment-backed at runtime.
        value={startTime ? dayjsToMoment(datetime(startTime, 'HH:mm')) : null}
        onSelect={(value) => onSelect(value)}
        onChange={(value) => onSelect(value)}
        popupClassName="no-footer-time-pick"
      />
      <style jsx>
        {`
          :global(.no-footer-time-pick .ant-picker-footer) {
            display: none;
          }
        `}
      </style>
    </>
  );
};
